import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
} from "react";
import type { ReactNode } from "react";
import {
  useLanguageDetection,
} from "../hooks/useLanguageDetection";
import type { Language, DetectionResult } from "../hooks/useLanguageDetection";
import { devLog } from "../utils/core/devLog";
import { publicLocaleFromPath } from '../data/publicSeo';

interface LanguageContextType extends DetectionResult {
  supportedLanguages: Language[];
  currentLanguage: string;
  currentLanguageName: string;
  changeLanguage: (languageCode: string) => void;
  isLanguageSupported: (languageCode: string) => boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

const getInitialLanguage = (supportedLanguages: Language[]) => {
  const fallback = supportedLanguages[0] ?? {
    language_code: "en",
    language_name: "English",
  };

  if (typeof window === "undefined") {
    return {
      code: fallback.language_code,
      name: fallback.language_name,
    };
  }

  const supported = (code: string | null | undefined) =>
    supportedLanguages.find(
      (lang) => lang.language_code === code?.toLowerCase().split("-")[0]
    );

  const pathMatch = supported(publicLocaleFromPath(window.location.pathname));
  if (pathMatch) return { code: pathMatch.language_code, name: pathMatch.language_name };

  const savedLanguage = localStorage.getItem("preferred-language");
  const savedMatch = supported(savedLanguage);
  if (savedMatch) {
    return {
      code: savedMatch.language_code,
      name: savedMatch.language_name,
    };
  }

  const browserCandidates = [
    ...(navigator.languages ?? []),
    navigator.language,
  ];
  const browserMatch = browserCandidates
    .map((code) => supported(code))
    .find(Boolean);

  if (browserMatch) {
    return {
      code: browserMatch.language_code,
      name: browserMatch.language_name,
    };
  }

  return {
    code: fallback.language_code,
    name: fallback.language_name,
  };
};

interface LanguageProviderProps {
  children: ReactNode;
  supportedLanguages: Language[];
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  supportedLanguages,
}) => {
  const detectionResult = useLanguageDetection(supportedLanguages, {
    debug: process.env['NODE_ENV'] === "development",
    enablePerformanceMonitoring: true,
  });

  const initialLanguage = getInitialLanguage(supportedLanguages);
  const [currentLanguage, setCurrentLanguage] = useState<string>(
    initialLanguage.code
  );
  const [currentLanguageName, setCurrentLanguageName] = useState<string>(
    initialLanguage.name
  );

  // Include the first render/browser-language choice, not just later changes.
  useLayoutEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Auto-set language when detection completes — only if user has no saved preference
  useEffect(() => {
    if (publicLocaleFromPath(window.location.pathname)) return;
    if (detectionResult.detectedLanguage && supportedLanguages.length > 0) {
      const savedLanguage = localStorage.getItem("preferred-language");
      const savedIsSupported =
        savedLanguage &&
        supportedLanguages.some((lang) => lang.language_code === savedLanguage);
      // Don't override a language the user explicitly selected
      if (!savedIsSupported) {
        setCurrentLanguage(detectionResult.detectedLanguage);
        setCurrentLanguageName(detectionResult.detectedLanguageName);
        document.documentElement.lang = detectionResult.detectedLanguage;
        localStorage.setItem(
          "preferred-language",
          detectionResult.detectedLanguage
        );
      }
    }
  }, [
    detectionResult.detectedLanguage,
    detectionResult.detectedLanguageName,
    supportedLanguages,
  ]);

  // Load preferred language from localStorage on mount
  useEffect(() => {
    if (publicLocaleFromPath(window.location.pathname)) return;
    const savedLanguage = localStorage.getItem("preferred-language");
    if (
      savedLanguage &&
      supportedLanguages.some((lang) => lang.language_code === savedLanguage)
    ) {
      setCurrentLanguage(savedLanguage);
      document.documentElement.lang = savedLanguage;
      const savedLang = supportedLanguages.find(
        (lang) => lang.language_code === savedLanguage
      );
      if (savedLang) {
        setCurrentLanguageName(savedLang.language_name);
      }
    }
  }, [supportedLanguages]);

  const changeLanguage = (languageCode: string) => {
    const selectedLang = supportedLanguages.find(
      (lang) => lang.language_code === languageCode
    );
    if (selectedLang) {
      setCurrentLanguage(languageCode);
      setCurrentLanguageName(selectedLang.language_name);
      document.documentElement.lang = languageCode;

      // Store in localStorage for persistence
      localStorage.setItem("preferred-language", languageCode);

      // Dispatch event for other components to listen to
      window.dispatchEvent(
        new CustomEvent("languageChanged", {
          detail: {
            languageCode,
            languageName: selectedLang.language_name,
            isSupported: detectionResult.isSupported,
          },
        })
      );

      devLog(
        `Language changed to: ${selectedLang.language_name} (${languageCode})`
      );
    }
  };

  const isLanguageSupported = (languageCode: string): boolean => {
    return supportedLanguages.some(
      (lang) => lang.language_code === languageCode
    );
  };

  const contextValue: LanguageContextType = {
    ...detectionResult,
    supportedLanguages,
    currentLanguage,
    currentLanguageName,
    changeLanguage,
    isLanguageSupported,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
